/**
 * A cardboard box: a dynamic rigid body the player can pick up and throw. The body
 * registers itself so `CarryController` can find it by id; `userData` is what the
 * interaction ray reads to offer "Pick up".
 *
 * Subscribes to focus with a boolean selector — it re-renders on gaining or losing the
 * crosshair (discrete, a few times a second at most), never per frame.
 */

import { CuboidCollider, RigidBody, type RapierRigidBody } from '@react-three/rapier';
import { useEffect, useRef } from 'react';
import { BackSide } from 'three';
import type { PlacedObject } from '../game/profile/buildGeometry';
import { useGameStore } from '../store/gameStore';
import { boxPosition, halfExtents } from './boxUnits';
import { getGradientMap } from './materials';
import { CARRYABLE_GROUPS, registerObjectBody, unregisterObjectBody } from './objectRegistry';
import type { BodyUserData } from './useInteractionTarget';

/**
 * A touch of deterministic irregularity: each box spawns slightly rotated by a hash of
 * its id, so a room of identical boxes still reads as clutter, not a grid.
 */
function spawnYaw(id: string): number {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) | 0;
  return ((hash % 100) / 100) * 0.6 - 0.3;
}

type Props = {
  object: PlacedObject;
  color: string;
};

export function CarryableBox({ object, color }: Props) {
  const bodyRef = useRef<RapierRigidBody>(null);
  const isFocused = useGameStore((store) => store.focus?.id === object.id);

  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    registerObjectBody(object.id, body);
    return () => unregisterObjectBody(object.id);
  }, [object.id]);

  const { size } = object.box;

  return (
    <RigidBody
      ref={bodyRef}
      colliders={false}
      position={boxPosition(object.box)}
      rotation={[0, spawnYaw(object.id), 0]}
      // A thrown box moves fast relative to its size; CCD keeps it from tunnelling
      // through walls or the ceiling in a single physics step.
      ccd
      userData={
        // The kind travels with the body so the interaction ray can report it: it is what
        // tells `availableAction` whether what is held fits what is looked at (design D5).
        { kind: 'carryable', id: object.id, objectKind: object.kind } satisfies BodyUserData
      }
    >
      <CuboidCollider args={halfExtents(object.box)} collisionGroups={CARRYABLE_GROUPS} />
      <mesh castShadow receiveShadow>
        <boxGeometry args={[size.x, size.y, size.z]} />
        <meshToonMaterial
          color={color}
          gradientMap={getGradientMap()}
          // Focus feedback is brightness plus the outline and prompt — never hue alone.
          emissive={isFocused ? color : '#000000'}
          emissiveIntensity={isFocused ? 0.35 : 0}
        />
      </mesh>
      {/* Packing-tape stripe over the top — the one detail that says "cardboard box". */}
      <mesh position={[0, size.y / 2 - 0.008, 0]} castShadow={false}>
        <boxGeometry args={[size.x * 0.28, 0.02, size.z * 1.001]} />
        <meshToonMaterial color="#a67c48" gradientMap={getGradientMap()} />
      </mesh>
      {isFocused && (
        <mesh scale={1.08}>
          <boxGeometry args={[size.x, size.y, size.z]} />
          <meshBasicMaterial color="#ffffff" side={BackSide} />
        </mesh>
      )}
    </RigidBody>
  );
}
